import { lazy, Suspense, useEffect, useMemo, useState } from 'react'
import { AlertTriangle, Layers } from 'lucide-react'

import { useAuth } from '../../auth/authContext'
import { useRoadbook } from '../../data/roadbookContext'
import { travelDayLabel } from '../../lib/calendar'
import { hours, num, zeile } from '../../lib/format'
import { routeState, stageAnchors } from '../../lib/route'
import { activeStages } from '../../lib/trip'
import { basemapBereich, basemapDeckt, decideMapMode, hasWebgl, type MapModeInput } from './basemap'
import { BasemapOffline } from './BasemapOffline'
import { differenzGenau, etappenVergleich, kilometerGenau } from './etappenVergleich'
import { RouteMap } from './RouteMap'
import { boundsOf, type Bounds } from './viewport'

// MapLibre ist mit Abstand die grösste Abhängigkeit dieser App. Es wird erst
// geladen, wenn wirklich jemand die Karte aufschlägt. Der erste Start der
// Anwendung merkt davon nichts.
const LiveMap = lazy(() => import('./LiveMap').then((m) => ({ default: m.LiveMap })))

/** Wo die Wahl der Kartenart liegt. Sie ist eine Vorliebe, keine Nutzdaten. */
const PREF_KEY = 'roadbook:karte-schematisch'

function readPreference(): boolean {
  try {
    return localStorage.getItem(PREF_KEY) === '1'
  } catch {
    // Privater Modus, gesperrter Speicher: dann eben die Vorgabe.
    return false
  }
}

interface Props {
  selectedId: string
  onSelect: (id: string) => void
}

export function MapView({ selectedId, onSelect }: Props) {
  const { trip, diary } = useRoadbook()
  const { config } = useAuth()
  const stages = activeStages(trip)
  const current = stages.find((s) => s.id === selectedId) ?? stages[0]

  const [prefersSchematic, setPrefersSchematic] = useState(readPreference)
  // Einmal fragen genügt: WebGL kommt nicht mitten in der Sitzung dazu.
  const [webgl] = useState(hasWebgl)

  const hasBasemap = config?.hasBasemap ?? false

  /**
   * Deckt das Archiv diese Reise ab?
   *
   * undefined heisst "noch nicht gefragt oder nicht zu ermitteln", und dann
   * bleibt es bei der echten Karte. Das Archiv wird bewusst nicht je Reise
   * gehalten - es ist zu gross dafür -, also kann eine zweite Reise
   * anderswohin führen als die, für die es geschnitten wurde. Ohne diese
   * Prüfung wäre die echte Karte dort eine graue Fläche mit einer Route
   * darauf, und MapLibre meldete dazu nichts: es bekommt seine Kacheln, sie
   * sind nur leer.
   */
  const [archiv, setArchiv] = useState<Bounds | null | undefined>(undefined)
  useEffect(() => {
    if (!hasBasemap || !webgl) return
    let abgebrochen = false
    void basemapBereich().then((bereich) => {
      if (!abgebrochen) setArchiv(bereich)
    })
    return () => {
      abgebrochen = true
    }
  }, [hasBasemap, webgl])

  // Mit den Zwischenzielen: sie gehören zur Reise, und die Frage lautet, ob das
  // Kachelarchiv sie deckt. Eine Runde, die dort endet, wo sie beginnt, wäre
  // ohne sie ein Punkt - und der liegt immer irgendwo im Archiv.
  const route = useMemo(() => boundsOf(stages.flatMap(stageAnchors)), [stages])
  const basemapPasst = archiv === undefined || basemapDeckt(archiv, route)

  const mode = decideMapMode({ hasBasemap, webgl, prefersSchematic, basemapPasst })

  // Nur eine aktuelle Strecke wird ausgewiesen: eine veraltete bekommt statt
  // einer Zahl den Hinweis, dass sie nicht mehr gilt.
  const aktuelleStrecke =
    current && routeState(current) === 'current' ? (current.route?.distanceKm ?? null) : null
  const vergleich = current ? etappenVergleich(current, diary[current.id]) : undefined

  const waehleArt = (schematisch: boolean) => {
    setPrefersSchematic(schematisch)
    try {
      localStorage.setItem(PREF_KEY, schematisch ? '1' : '0')
    } catch {
      // Nicht speicherbar ist kein Grund, die Umschaltung zu verweigern.
    }
  }

  if (stages.length === 0) {
    return (
      <section className="seite">
        <p className="leer">
          Noch keine Etappe geplant. Die Karte zeichnet die Route aus den Koordinaten der Etappen —
          sobald die erste steht, steht sie hier.
        </p>
      </section>
    )
  }

  const schematisch = (
    <RouteMap stages={stages} diary={diary} selectedId={selectedId} onSelect={onSelect} />
  )

  return (
    <section className="seite">
      {mode === 'live' ? (
        <>
          <Suspense fallback={<p className="leer">Karte wird geladen …</p>}>
            <LiveMap stages={stages} diary={diary} selectedId={selectedId} onSelect={onSelect} />
          </Suspense>
          {/*
            Die schematische Karte bleibt als Druckfassung im Baum. Eine
            WebGL-Fläche kommt je nach Browser leer oder gar nicht aufs Papier;
            die schematische ist SVG und druckt sicher. Sichtbar wird sie nur im
            Druck, siehe print.css.
          */}
          <div className="karte-druckfassung">{schematisch}</div>
        </>
      ) : (
        schematisch
      )}

      <div className="legende">
        <span>
          <i className="gefahren" /> gefahren
        </span>
        <span>
          <i className="geplant" /> geplant
        </span>
        {/*
          Die dritte Zeile ist der Kern der Sache: durchgezogen zeigt die
          Strasse, wie ein Dienst sie berechnet hat, gestrichelt nur die
          Verbindung zweier Punkte. Ohne diese Erklärung wäre der Unterschied
          im Bild sichtbar, aber nicht lesbar.
        */}
        <span>
          <i className="luftlinie" /> Luftlinie, Strecke nicht berechnet
        </span>
        {/*
          Der vierte Fall: kein Rechenergebnis, sondern eine Messung. Er
          bekommt keine eigene Farbe und keine eigene Strichelung, sondern eine
          Hülle unter der Linie - so bleibt die bestehende Unterscheidung
          unangetastet und der Track ist trotzdem zu erkennen.
        */}
        <span>
          <i className="aufgezeichnet" /> aufgezeichnet, GPX aus dem Tagebuch
        </span>
        <span className="kein-druck">Punkt oder Linie antippen wählt die Etappe</span>
        {hasBasemap && webgl && (
          <button
            type="button"
            className="knopf still kein-druck karte-umschalter"
            aria-pressed={prefersSchematic}
            onClick={() => {
              waehleArt(!prefersSchematic)
            }}
          >
            <Layers size={13} /> {prefersSchematic ? 'echte Karte' : 'schematisch'}
          </button>
        )}
      </div>

      {current && (
        <div className="kartenkarte">
          <span className="tagnummer gross">{travelDayLabel(stages, current)}</span>
          <div>
            <h3>
              {current.from} → {current.to}
            </h3>
            <p>
              {/* Das Land ist ein Freitextfeld und in einer frisch angelegten
                  Etappe leer - dann steht dort auch kein Trennpunkt. */}
              {zeile([`${num(current.km)} km`, `${hours(current.h)} h`, current.land])}
              {/*
                Woher die Linie kommt, gehört an die Etappe und nicht nur in die
                Legende. Die Zahl daneben ist die berechnete Strecke, nicht der
                Handwert links - stimmen beide nicht zusammen, sieht man es hier.
              */}
              {vergleich
                ? ` · gefahren (GPX) ${kilometerGenau(vergleich.gefahrenKm)} km` +
                  ` · geplant (Route) ${kilometerGenau(vergleich.geplantKm)} km` +
                  ` · Differenz ${differenzGenau(vergleich.differenzKm)} km`
                : aktuelleStrecke !== null && ` · Strecke ${num(aktuelleStrecke)} km berechnet`}
              {routeState(current) === 'stale' && ' · berechnete Strecke veraltet'}
            </p>
            {current.tip && (
              <p className="hinweis">
                <AlertTriangle size={13} /> {current.tip}
              </p>
            )}
          </div>
        </div>
      )}

      {/*
        Ohne Bedingung eingehängt: liegt auf dem Server kein Archiv mehr, kann
        die lokale Kopie trotzdem noch da sein, und die muss löschbar bleiben.
        Was zu sehen ist, entscheidet die Komponente selbst.
      */}
      <BasemapOffline hasBasemap={hasBasemap} />

      <p className="abschluss">
        {mode === 'live'
          ? 'Die echte Karte zeigt Vektorkacheln aus einem PMTiles-Archiv auf dem eigenen Server, ' +
            'gerendert von MapLibre. Die Kartendaten stammen von OpenStreetMap und stehen unter der ' +
            'ODbL, deshalb steht die Namensnennung fest in der Karte. Ohne heruntergeladenes Archiv ' +
            'braucht sie Netz. Für den Druck und als Rückfallebene bleibt die schematische Karte.'
          : `${grundFuerSchematisch({ hasBasemap, webgl, prefersSchematic, basemapPasst })} ` +
            'Die schematische Karte ist eine Mercator-Projektion ohne Kartendienst: sie zeigt ' +
            'Route, Etappenpunkte und ein Gradnetz, und sie funktioniert offline und im Druck.'}
      </p>
    </section>
  )
}

/**
 * Sagt, warum gerade die schematische Karte zu sehen ist.
 *
 * Der Grund gehört dazu, und der letzte Fall ist der, der sonst niemandem
 * erklärlich wäre: das Archiv liegt da, WebGL geht, und trotzdem kommt die
 * schematische Karte - weil das Archiv eine andere Gegend zeigt als die, in
 * die diese Reise führt.
 */
function grundFuerSchematisch(input: Required<MapModeInput>): string {
  if (input.prefersSchematic) return 'Schematische Karte, weil sie hier ausgewählt ist.'
  if (!input.webgl) return 'Dieser Browser kann kein WebGL, deshalb die schematische Karte.'
  if (!input.hasBasemap) {
    return 'Auf dem Server liegt kein Kartenarchiv, deshalb die schematische Karte.'
  }
  if (!input.basemapPasst) {
    return (
      'Das Kartenarchiv auf dem Server deckt diese Gegend nicht ab — es ist für eine andere ' +
      'Route geschnitten. Deshalb die schematische Karte; die echte wäre hier eine graue Fläche.'
    )
  }
  return ''
}
